import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';

type InjectedScriptParams = {
  callback: () => void;
  script: string;
  style: string;
};

const openInBrowser = async (
  url: string | null | undefined,
  target = '_blank',
  injectedScriptParams?: InjectedScriptParams,
) => {
  if (!url) {
    console.error('Cannot open undefined URL.');
    return;
  }

  const isHTTPS = /^https:[/][/]/.test(url);

  /**
   * Script injection is only supported for HTTPS URLs due to CSP.
   */
  if (injectedScriptParams && isHTTPS) {
    const i18n = getI18n();
    const { InAppBrowser } = require('@awesome-cordova-plugins/in-app-browser');

    const browser = InAppBrowser.create(url, '_blank', {
      location: 'no',
      closebuttoncaption: i18n.t('common:close'),
      disallowoverscroll: 'yes',
      hidenavigationbuttons: 'yes',
      fullscreen: 'yes',
    });

    const onLoaded = () => {
      browser.executeScript({
        code: injectedScriptParams.script,
      });

      browser.insertCSS({
        code: injectedScriptParams.style,
      });

      browser.show();
    };

    browser.on('loadstop').subscribe(() => {
      onLoaded();
    });

    browser.on('message').subscribe(() => {
      browser.close();
      injectedScriptParams.callback();
    });
  } else if (Capacitor.isNativePlatform()) {
    Browser.open({ url, windowName: target });
  } else {
    // On web, call window.open() directly. Capacitor's registerPlugin proxy
    // wraps every call in a loadPluginImplementation().then() chain, deferring
    // window.open() to a microtask. Mobile browsers block window.open() that
    // doesn't execute synchronously within the user gesture callstack.
    window.open(url, target);
  }
};

export default openInBrowser;
